import React, {useEffect, useState} from "react";
import {
    Body1,
    Body1Strong,
    Button,
    Card,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    makeStyles,
    MessageBar,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellActions,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    Title3,
    tokens
} from "@fluentui/react-components";
import {
    ArrowSync24Regular,
    CloudArrowUp24Regular,
    Delete24Regular,
    Dismiss24Regular,
    DocumentTableArrowRight24Regular,
    LockShield16Regular,
    PeopleTeam24Regular,
    TextBulletListAdd24Regular
} from "@fluentui/react-icons";
import {useAtomValue, useSetAtom} from "jotai/index";
import {errorAtom, groupsTabViewAtom, teamsGroupLoadingAtom} from "../../../store";
import {useApiMutation, useApiQuery} from "../../../services/useApiQuery.ts";
import {Group} from "../../../services/types.ts";
import {useTranslation} from "react-i18next";
import {AudienceMembersModal} from "../statusModal/AudienceMembersModal.tsx";

// @ts-ignore
const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '16px',
        '@media (max-width: 640px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        padding: '16px',
    },
    optionsContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        margin: '8px 0',
    },
    headerButtonsContainer: {
        display: 'flex',
        gap: '8px',
        '@media (max-width: 640px)': {
            width: '100%',
            flexDirection: 'column',
            '& button': {
                width: '100%',
            },
        },
    },
    optionButton: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '32px',
        borderRadius: '8px',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        cursor: 'pointer',
        width: '250px',
        gap: '16px',
        backgroundColor: tokens.colorNeutralBackground1,
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    optionIcon: {
        fontSize: '48px',
        color: tokens.colorBrandForeground1,
    },
    optionTitle: {
        fontWeight: 'bold',
        textAlign: 'center',
    },
    optionDescription: {
        color: tokens.colorNeutralForeground2,
        textAlign: 'center',
        fontSize: '14px',
    },
    optionDescriptionEmphasis: {
        color: tokens.colorStatusWarningForeground3,
        textAlign: 'center',
        textEmphasis: 'strong',
        fontSize: '14px',
        marginTop: "auto",
    },
    closeButton: {
        position: 'absolute',
        top: '12px',
        right: '12px',
        cursor: 'pointer',
    },
    deleteDialog: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    messageBar: {
        margin: '10px'
    },
    dialogActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
        marginTop: '16px',
    },
    responsiveTable: {
        '@media (max-width: 640px)': {
            '& table': {
                display: 'block',
                width: '100%',
            },
            '& thead': {
                display: 'none',
            },
            '& tbody': {
                display: 'block',
                width: '100%',
            },
            '& tr': {
                display: 'flex',
                flexDirection: 'column',
                padding: '12px 0',
                marginBottom: '8px',
                '&:not(:last-child)': {
                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    paddingBottom: '16px',
                },
            },
            '& td': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: 'none',
                padding: '4px 0',
                width: '100%',
                position: 'relative',
                '&::before': {
                    content: 'attr(data-label)',
                    fontWeight: 'bold',
                    marginRight: '8px',
                },
                '&:last-child': {
                    justifyContent: 'flex-end',
                    marginTop: '8px',
                },
            },
            '& [data-label="Actions"]': {
                paddingTop: '8px',
                marginTop: '8px',
            },
            '& [role="cell-actions"]': {
                opacity: '1 !important',
                // @ts-ignore
                position: 'static !important',
                transform: 'none !important',
                padding: '0 !important',
            },
        },
    },
});

export const AudienceListView: React.FC = () => {
    const styles = useStyles();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isManageModalOpen, setIsManageModalOpen] = useState<boolean>(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
    const [isiOS, setIsiOS] = useState<boolean>(false);
    const {t} = useTranslation()

    useEffect(() => {
        const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        setIsiOS(isiOS)
    }, []);

    const setError = useSetAtom(errorAtom);
    const setGroupView = useSetAtom(groupsTabViewAtom);
    const createGroupInProgress = useAtomValue(teamsGroupLoadingAtom)

    const {
        data: groups,
        isLoading: groupsLoading,
        isRefetching: isRefetchingGroups,
        refetch: refetchGroups,
    } = useApiQuery<Group[]>('/api/group/list');

    const deleteGroupMutation = useApiMutation<void, { id: string }>('DELETE', {
        onSuccess: async () => {
            await refetchGroups();
            setSelectedGroup(null);
            setError(null);
        },
        onError: (error) => {
            setError(`Failed to delete group: ${error.message}`);
        }
    });

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleOpenCreateModal = () => {
        setIsModalOpen(true);
    };

    const handleLoadFromTeams = () => {
        setIsModalOpen(false);
        setGroupView('addGroupTeams');
    };

    const handleLoadIndividually = () => {
        setIsModalOpen(false);
        setGroupView('addGroupIndividuals');
    };

    const handleLoadManually = () => {
        setIsModalOpen(false);
        setGroupView('addGroupManual');
    };

    const handleDeleteClick = (group: Group) => {
        setSelectedGroup(group);
        setIsDeleteModalOpen(true);
    };

    const handleManageClick = (group: Group) => {
        setSelectedGroup(group);
        setIsManageModalOpen(true)
    }

    const handleConfirmDelete = () => {
        if (selectedGroup) {
            deleteGroupMutation.mutate({
                path: '/api/group/delete',
                body: {id: selectedGroup.id}
            });
            setIsDeleteModalOpen(false);
        }
    };

    const handleCancelDelete = () => {
        setSelectedGroup(null);
        setIsDeleteModalOpen(false);
    };

    const inDeletionGroupId = deleteGroupMutation.isPending && selectedGroup?.id;

    return <>
        <Card>
            <div className={styles.container}>
                {isiOS && <MessageBar intent="info">
                    <Body1 className={styles.messageBar}>{t("message.audiences-limited-ios")}</Body1>
                </MessageBar>}
                <div className={styles.header}>
                    <Title3>{t("nav.audiences")}</Title3>
                    <div className={styles.headerButtonsContainer}>
                        <Button appearance="primary" icon={<DocumentTableArrowRight24Regular/>}
                                onClick={handleOpenCreateModal}
                                disabled={isiOS}
                        >
                            {t("button.create-new-audience")}
                        </Button>
                        <Button
                            appearance="subtle"
                            icon={(groupsLoading || isRefetchingGroups || createGroupInProgress || deleteGroupMutation.isPending) ?
                                <Spinner size="extra-tiny"/> : <ArrowSync24Regular/>}
                            onClick={() => {
                                refetchGroups();
                            }}
                            disabled={isRefetchingGroups || groupsLoading}
                        >
                            {t("button.refresh")}
                        </Button>
                    </div>
                </div>

                {(isRefetchingGroups || groupsLoading || createGroupInProgress) && !deleteGroupMutation.isPending ?
                    <Spinner label={t("text.loading-audiences")}/> :
                    groups?.length === 0 ? (
                        <Text>{t("text.no-audiences")}</Text>
                    ) : (
                        <div className={styles.responsiveTable}>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell>{t("table.header.name")}</TableHeaderCell>
                                        <TableHeaderCell>{t("table.header.action")}</TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {groups?.map((group: any) => (
                                        <TableRow key={group.id}>
                                            <TableCell data-label={t("table.header.name")}>{group.name}</TableCell>
                                            <TableCell data-label={t("table.header.action")}
                                                       style={{textAlign: 'right'}}>
                                                <TableCellActions role="cell-actions">
                                                    <Button
                                                        style={{marginLeft: "8px"}}
                                                        appearance="secondary"
                                                        disabled={isRefetchingGroups || createGroupInProgress || deleteGroupMutation.isPending}
                                                        onClick={() => handleManageClick(group)}
                                                    >
                                                        {t("button.manage")}
                                                    </Button>
                                                    <Button
                                                        style={{marginLeft: "8px"}}
                                                        appearance="secondary"
                                                        icon={group.id === inDeletionGroupId ?
                                                            <Spinner size="extra-tiny"></Spinner>
                                                            : <Delete24Regular/>}
                                                        disabled={groupsLoading || isRefetchingGroups || createGroupInProgress || deleteGroupMutation.isPending}
                                                        onClick={() => handleDeleteClick(group)}
                                                    >
                                                        {t("button.delete")}
                                                    </Button>
                                                </TableCellActions>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
            </div>
        </Card>

        <Dialog open={isModalOpen} onOpenChange={(_, data) => !data.open && handleCloseModal()}>
            <DialogSurface>
                <DialogBody>
                    <Dismiss24Regular
                        className={styles.closeButton}
                        onClick={handleCloseModal}
                    />
                    <Title3>{t("button.create-new-audience")}</Title3>
                    <DialogContent className={styles.modalContent}>
                        <Text>{t("text.choose-participants-add-method")}</Text>

                        <div className={styles.optionsContainer}>
                            <div className={styles.optionButton} onClick={handleLoadFromTeams}>
                                <PeopleTeam24Regular className={styles.optionIcon}/>
                                <Text className={styles.optionTitle}>{t("text.load-from-teams")}</Text>
                                <Body1Strong className={styles.optionDescriptionEmphasis}>
                                    <div className="flex">
                                        <div className=" items-center"><LockShield16Regular/></div>
                                        {t("text.load-from-teams-permissions")}</div>
                                </Body1Strong>
                            </div>

                            <div className={styles.optionButton} onClick={handleLoadIndividually}>
                                <CloudArrowUp24Regular className={styles.optionIcon}/>
                                <Text className={styles.optionTitle}>{t('text.find-by-name')}</Text>
                                <Body1Strong className={styles.optionDescriptionEmphasis}>
                                    <div className="flex">
                                        <div className=" items-center"><LockShield16Regular/></div>
                                        {t("text.load-from-teams-permissions")}</div>
                                </Body1Strong>
                            </div>

                            <div className={styles.optionButton} onClick={handleLoadManually}>
                                <TextBulletListAdd24Regular className={styles.optionIcon}/>
                                <Text className={styles.optionTitle}>{t('text.add-users-manually')}</Text>
                            </div>
                        </div>
                    </DialogContent>
                </DialogBody>
            </DialogSurface>
        </Dialog>

        <Dialog open={isManageModalOpen} onOpenChange={(_, data) => !data.open && setIsManageModalOpen(false)}>
            <DialogSurface>
                <DialogContent>
                    <Dismiss24Regular
                        className={styles.closeButton}
                        onClick={setIsManageModalOpen.bind(this, false)}
                    />
                    <DialogBody style={{gridTemplateColumns: 'unset'}}>
                        <AudienceMembersModal isiOS={isiOS}  groupId={selectedGroup?.id!}></AudienceMembersModal>
                    </DialogBody>
                </DialogContent>
            </DialogSurface>
        </Dialog>

        <Dialog open={isDeleteModalOpen} onOpenChange={(_, data) => !data.open && handleCancelDelete()}>
            <DialogSurface>
                <DialogBody>
                    <Title3>{t("text.delete-audience")}</Title3>
                    <DialogContent>
                        <div className={styles.deleteDialog}>
                            <Text>
                                {t("text.are-you-sure-delete")} "{selectedGroup?.name}"?
                                <br/>{t("text.cannot-be-undone")}
                            </Text>

                            <div className={styles.dialogActions}>
                                <Button
                                    appearance="secondary"
                                    onClick={handleCancelDelete}
                                >
                                    {t("button.cancel")}
                                </Button>
                                <Button
                                    appearance="primary"
                                    onClick={handleConfirmDelete}
                                    style={{backgroundColor: tokens.colorPaletteRedBackground3}}>
                                    {t("button.delete")}
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    </>
}
